import client from "@/api/openapi-ts";
import type { components, paths } from "@/api/schema.d.ts";
import { downloadFile, mimeTypeForFilename } from "@/lib/exports/downloadFile";
import { defineStore, acceptHMRUpdate } from "pinia";
import { ref } from "vue";

type schemas = components["schemas"];
type FilesListQuery = NonNullable<
  paths["/api/v1/files"]["get"]["parameters"]["query"]
>;

export const FILE_PAGE_SIZE = 100;

export type FileRecord = schemas["FilesListResponse"];
export type FileActivityResponse = schemas["FileActivityResponse"];
export type FileActivityRecipient = schemas["FileActivityRecipient"];
export type FileFilterOptions = {
  accounts: string[];
  sources: string[];
  created_by: string[];
  am_uploaders: string[];
};
export type ListFilesOptions = {
  admin?: boolean;
  q?: string;
  createdAfter?: string;
  createdBefore?: string;
  accounts?: string[];
  sources?: string[];
  types?: string[];
  createdBy?: string[];
  amUploaders?: string[];
};

function parseHeaderInt(
  headers: Headers | undefined,
  name: string,
): number | undefined {
  const raw = headers?.get(name);
  if (raw == null || raw === "") return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}

function buildFilesListQuery(
  options: ListFilesOptions,
  offset: number,
): FilesListQuery {
  const query: FilesListQuery = {
    limit: FILE_PAGE_SIZE,
    offset,
  };
  const q = options.q?.trim();
  if (q) query.q = q;
  if (options.createdAfter) query.created_after = options.createdAfter;
  if (options.createdBefore) query.created_before = options.createdBefore;
  if (options.accounts?.length) query["accounts[]"] = options.accounts;
  if (options.sources?.length) query["sources[]"] = options.sources;
  if (options.types?.length) query["types[]"] = options.types;
  if (options.createdBy?.length) query["created_by[]"] = options.createdBy;
  if (options.amUploaders?.length)
    query["am_uploaders[]"] = options.amUploaders;
  return query;
}

export type DeliveryEmailTemplate = Pick<
  schemas["AmDeliveryEmailTemplateResponse"],
  "id" | "name" | "subject" | "body"
>;
export type DeliveryEmailLastSend = schemas["AmDeliveryEmailLastSendResponse"];

function toDeliveryEmailTemplate(
  row: schemas["AmDeliveryEmailTemplateResponse"],
): DeliveryEmailTemplate {
  return {
    id: row.id,
    name: row.name,
    subject: row.subject,
    body: row.body ?? "",
  };
}

export const useFileStore = defineStore("files", () => {
  const loading = ref(false);
  const loadingMore = ref(false);
  const hasMore = ref(false);
  const uploading = ref(false);
  const files = ref<FileRecord[]>([]);
  const filterOptions = ref<FileFilterOptions>({
    accounts: [],
    sources: [],
    created_by: [],
    am_uploaders: [],
  });
  const knowledgeGuides = ref<FileRecord[]>([]);
  const knowledgeGuidesLoading = ref(false);
  const deliveryEmailTemplates = ref<DeliveryEmailTemplate[]>([]);
  const deliveryEmailTemplatesLoading = ref(false);

  // Serializes read-modify-write updates so concurrent uploadExport calls (e.g.
  // multiple exportCsv persists in one download) cannot drop each other's entries.
  let fileListMutation = Promise.resolve();

  // Invalidates in-flight listFiles when clearFiles runs or a newer list starts,
  // so a stale response cannot repopulate after an org/app-client switch.
  let listFilesGeneration = 0;
  let listFilterOptionsGeneration = 0;
  let listKnowledgeGuidesGeneration = 0;
  let nextOffset = 0;
  let activeListOptions: ListFilesOptions = {};

  // Invalidates in-flight template lists when a mutation starts or a newer list
  // starts, so a stale GET cannot overwrite create/update/delete results.
  let deliveryEmailTemplatesGeneration = 0;

  // Share mutations replace the whole file record; queue per file so an older
  // PATCH/DELETE response cannot overwrite a newer share state.
  const fileShareChains = new Map<number, Promise<unknown>>();

  function mutateFiles(
    mutator: (current: FileRecord[]) => FileRecord[],
  ): Promise<void> {
    const mutation = fileListMutation.then(() => {
      files.value = mutator(files.value);
    });
    // Recover the queue after a failed mutation so later updates still run.
    fileListMutation = mutation.catch(() => {});
    return mutation;
  }

  function enqueueFileShareMutation<T>(
    fileId: number,
    task: () => Promise<T>,
  ): Promise<T> {
    const previous = fileShareChains.get(fileId) ?? Promise.resolve();
    const next = previous.catch(() => {}).then(task);
    fileShareChains.set(fileId, next);
    return next;
  }

  function prependFile(record: FileRecord): Promise<void> {
    return mutateFiles((current) => [
      record,
      ...current.filter((file) => file.id !== record.id),
    ]);
  }

  async function fetchFilesPage(options: ListFilesOptions, offset: number) {
    const query = buildFilesListQuery(options, offset);
    return options.admin
      ? client.GET("/api/v1/files/admin", { params: { query } })
      : client.GET("/api/v1/files", { params: { query } });
  }

  function applyHasMore(
    fetchedCount: number,
    loadedCount: number,
    total?: number,
  ) {
    hasMore.value =
      total != null ? loadedCount < total : fetchedCount >= FILE_PAGE_SIZE;
  }

  async function listFiles(options: ListFilesOptions = {}) {
    const generation = ++listFilesGeneration;
    loading.value = true;
    loadingMore.value = false;
    hasMore.value = false;
    nextOffset = 0;
    activeListOptions = { ...options };
    // Only preserve files prepended while this fetch is in flight (e.g.
    // concurrent uploadExport). Files already present before the request —
    // including stale entries from a previous org/app_client — must be dropped
    // when the server no longer returns them.
    const idsBeforeFetch = new Set(files.value.map((file) => file.id));
    try {
      const { data, error, response } = await fetchFilesPage(options, 0);
      if (error) throw error;
      if (generation !== listFilesGeneration) return;
      const serverFiles = data ?? [];
      const total = parseHeaderInt(response?.headers, "total-count");
      await mutateFiles((current) => {
        if (generation !== listFilesGeneration) return current;
        const serverIds = new Set(serverFiles.map((file) => file.id));
        const addedWhileFetching = current.filter(
          (file) => !serverIds.has(file.id) && !idsBeforeFetch.has(file.id),
        );
        return [...addedWhileFetching, ...serverFiles];
      });
      if (generation !== listFilesGeneration) return;
      nextOffset = serverFiles.length;
      applyHasMore(serverFiles.length, nextOffset, total);
    } finally {
      if (generation === listFilesGeneration) {
        loading.value = false;
      }
    }
  }

  async function listMoreFiles() {
    if (loading.value || loadingMore.value || !hasMore.value) return;
    const generation = listFilesGeneration;
    const options = activeListOptions;
    const offset = nextOffset;
    loadingMore.value = true;
    try {
      const { data, error, response } = await fetchFilesPage(options, offset);
      if (error) throw error;
      if (generation !== listFilesGeneration) return;
      const page = data ?? [];
      const total = parseHeaderInt(response?.headers, "total-count");
      await mutateFiles((current) => {
        if (generation !== listFilesGeneration) return current;
        const seen = new Set(current.map((file) => file.id));
        return [...current, ...page.filter((file) => !seen.has(file.id))];
      });
      if (generation !== listFilesGeneration) return;
      nextOffset = offset + page.length;
      applyHasMore(page.length, nextOffset, total);
    } finally {
      if (generation === listFilesGeneration) {
        loadingMore.value = false;
      }
    }
  }

  const emptyFilterOptions = (): FileFilterOptions => ({
    accounts: [],
    sources: [],
    created_by: [],
    am_uploaders: [],
  });

  async function listFileFilterOptions(admin = false) {
    const generation = ++listFilterOptionsGeneration;
    try {
      const { data, error } = admin
        ? await client.GET("/api/v1/files/admin/filter-options")
        : await client.GET("/api/v1/files/filter-options");
      if (error) throw error;
      if (generation !== listFilterOptionsGeneration) return;
      filterOptions.value = {
        accounts: data?.accounts ?? [],
        sources: data?.sources ?? [],
        created_by: data?.created_by ?? [],
        am_uploaders: data?.am_uploaders ?? [],
      };
    } catch {
      if (generation !== listFilterOptionsGeneration) return;
      filterOptions.value = emptyFilterOptions();
    }
  }

  function resetFilesList() {
    listFilesGeneration += 1;
    loading.value = true;
    loadingMore.value = false;
    hasMore.value = false;
    nextOffset = 0;
    activeListOptions = {};
    files.value = [];
    return mutateFiles(() => []);
  }

  function clearFiles() {
    listFilesGeneration += 1;
    listFilterOptionsGeneration += 1;
    listKnowledgeGuidesGeneration += 1;
    loading.value = false;
    loadingMore.value = false;
    hasMore.value = false;
    nextOffset = 0;
    activeListOptions = {};
    knowledgeGuidesLoading.value = false;
    knowledgeGuides.value = [];
    files.value = [];
    filterOptions.value = emptyFilterOptions();
    return mutateFiles(() => []);
  }

  function mutateKnowledgeGuides(
    mutator: (current: FileRecord[]) => FileRecord[],
  ): void {
    knowledgeGuides.value = mutator(knowledgeGuides.value);
  }

  function prependKnowledgeGuide(record: FileRecord): void {
    mutateKnowledgeGuides((current) => [
      record,
      ...current.filter((file) => file.id !== record.id),
    ]);
  }

  function replaceKnowledgeGuide(record: FileRecord): void {
    mutateKnowledgeGuides((current) =>
      current.map((file) => (file.id === record.id ? record : file)),
    );
  }

  function removeKnowledgeGuide(fileId: number): void {
    mutateKnowledgeGuides((current) =>
      current.filter((file) => file.id !== fileId),
    );
  }

  function renameKnowledgeGuideProduct(from: string, to: string): void {
    const fromLower = from.trim().toLowerCase();
    mutateKnowledgeGuides((current) =>
      current.map((file) => {
        const product = file.meta_data?.product;
        if (
          typeof product !== "string" ||
          product.trim().toLowerCase() !== fromLower
        ) {
          return file;
        }
        return {
          ...file,
          meta_data: { ...file.meta_data, product: to },
        };
      }),
    );
  }

  async function listKnowledgeGuides() {
    const generation = ++listKnowledgeGuidesGeneration;
    knowledgeGuidesLoading.value = true;
    // Only preserve guides prepended while this fetch is in flight (e.g.
    // concurrent uploadKnowledgeGuide). Guides already present before the
    // request — including stale entries from a previous org/app_client —
    // must be dropped when the server no longer returns them.
    const idsBeforeFetch = new Set(
      knowledgeGuides.value.map((file) => file.id),
    );
    try {
      const { data, error } = await client.GET(
        "/api/v1/files/knowledge-guides",
      );
      if (error) throw error;
      if (generation !== listKnowledgeGuidesGeneration) return;
      const serverGuides = data ?? [];
      mutateKnowledgeGuides((current) => {
        if (generation !== listKnowledgeGuidesGeneration) return current;
        const serverIds = new Set(serverGuides.map((file) => file.id));
        const addedWhileFetching = current.filter(
          (file) => !serverIds.has(file.id) && !idsBeforeFetch.has(file.id),
        );
        return [...addedWhileFetching, ...serverGuides];
      });
    } finally {
      if (generation === listKnowledgeGuidesGeneration) {
        knowledgeGuidesLoading.value = false;
      }
    }
  }

  async function downloadFileById(fileId: number, fileName: string) {
    const { data, error } = await client.GET("/api/v1/files/{file_id}", {
      params: { path: { file_id: fileId } },
      parseAs: "arrayBuffer",
    });
    if (error) throw error;
    if (!data) throw new Error(`No data returned for ${fileName}`);
    downloadFile(fileName, data, mimeTypeForFilename(fileName));
  }

  async function openFileInNewTab(fileId: number, fileName: string) {
    const { data, error } = await client.GET("/api/v1/files/{file_id}", {
      params: { path: { file_id: fileId } },
      parseAs: "arrayBuffer",
    });
    if (error) throw error;
    if (!data) throw new Error(`No data returned for ${fileName}`);
    const blob = new Blob([data], {
      type: mimeTypeForFilename(fileName),
    });
    const url = window.URL.createObjectURL(blob);
    const opened = window.open(url, "_blank");
    if (!opened) {
      window.URL.revokeObjectURL(url);
      throw new Error(
        "Unable to open file. Please allow pop-ups for this site.",
      );
    }
    // Keep the blob URL alive long enough for the new tab to load the PDF.
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  }

  async function uploadDeliverable(
    file: File,
    options?: {
      fileName?: string;
      userIds?: number[];
      metaData?: Record<string, unknown>;
    },
  ) {
    uploading.value = true;
    const formData = new FormData();
    formData.append("file", file, options?.fileName?.trim() || file.name);
    if (options?.userIds?.length) {
      formData.append("user_ids", JSON.stringify(options.userIds));
    }
    if (options?.metaData && Object.keys(options.metaData).length > 0) {
      formData.append("meta_data", JSON.stringify(options.metaData));
    }
    try {
      const { data, error } = await client.POST("/api/v1/files", {
        body: formData as never,
      });
      if (error) throw error;
      if (!data) throw new Error("Upload failed");
      await prependFile(data);
      return data;
    } finally {
      uploading.value = false;
    }
  }

  async function uploadKnowledgeGuide(
    file: File,
    options?: {
      metaData?: Record<string, unknown>;
    },
  ) {
    uploading.value = true;
    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("feature", "knowledge-guide");
    if (options?.metaData && Object.keys(options.metaData).length > 0) {
      formData.append("meta_data", JSON.stringify(options.metaData));
    }
    try {
      const { data, error } = await client.POST("/api/v1/files", {
        body: formData as never,
      });
      if (error) throw error;
      if (!data) throw new Error("Upload failed");
      prependKnowledgeGuide(data);
      return data;
    } finally {
      uploading.value = false;
    }
  }

  async function updateKnowledgeGuide(
    fileId: number,
    options?: {
      file?: File;
      metaData?: Record<string, unknown>;
    },
  ) {
    const hasFile = Boolean(options?.file);
    const hasMetaData = Boolean(
      options?.metaData && Object.keys(options.metaData).length > 0,
    );
    if (!hasFile && !hasMetaData) {
      return;
    }
    uploading.value = true;
    const formData = new FormData();
    if (options?.file) {
      formData.append("file", options.file, options.file.name);
    }
    if (options?.metaData && Object.keys(options.metaData).length > 0) {
      formData.append("meta_data", JSON.stringify(options.metaData));
    }
    try {
      const { data, error } = await client.PUT(
        "/api/v1/files/knowledge-guides/{file_id}",
        {
          params: { path: { file_id: fileId } },
          body: formData as never,
        },
      );
      if (error) throw error;
      if (!data) throw new Error("Update failed");
      replaceKnowledgeGuide(data);
      return data;
    } finally {
      uploading.value = false;
    }
  }

  async function uploadExport(
    blob: Blob,
    filename: string,
    feature: string,
    meta_data?: Record<string, unknown>,
  ) {
    const formData = new FormData();
    formData.append("file", blob, filename);
    formData.append("feature", feature);
    if (meta_data && Object.keys(meta_data).length > 0) {
      formData.append("meta_data", JSON.stringify(meta_data));
    }
    const { data, error } = await client.POST("/api/v1/files", {
      body: formData as never,
    });
    if (error) throw error;
    if (!data) throw new Error("Upload failed");
    await prependFile(data);
    return data;
  }

  async function updateFileName(fileId: number, fileName: string) {
    const { data, error } = await client.PATCH("/api/v1/files/{file_id}", {
      params: { path: { file_id: fileId } },
      body: { file_name: fileName },
    });
    if (error) throw error;
    if (!data) throw new Error("Update failed");
    await mutateFiles((current) =>
      current.map((file) => (file.id === fileId ? data : file)),
    );
    return data;
  }

  async function shareFile(
    fileId: number,
    payload: {
      userIds: number[];
      role: "viewer" | "sharer" | "editor";
      ccUserIds?: number[];
      emailReplyTo?: string;
      emailSubject?: string;
      emailBody?: string;
      templateId?: number | null;
      includeDownloadsLink?: boolean;
      attachFileDirectly?: boolean;
    },
  ) {
    return enqueueFileShareMutation(fileId, async () => {
      const { data, error } = await client.POST(
        "/api/v1/files/{file_id}/share",
        {
          params: { path: { file_id: fileId } },
          body: {
            user_ids: payload.userIds,
            role: payload.role,
            cc_user_ids: payload.ccUserIds ?? [],
            email_from: null,
            email_reply_to: payload.emailReplyTo ?? null,
            email_subject: payload.emailSubject ?? null,
            email_body: payload.emailBody ?? null,
            template_id: payload.templateId ?? null,
            include_downloads_link: payload.includeDownloadsLink ?? true,
            attach_file_directly: payload.attachFileDirectly ?? true,
          },
        },
      );
      if (error) throw error;
      if (!data) throw new Error("Share failed");
      await mutateFiles((current) =>
        current.map((file) => (file.id === fileId ? data : file)),
      );
      return data;
    });
  }

  async function updateSharePermission(
    fileId: number,
    userId: number,
    role: "viewer" | "sharer" | "editor",
  ) {
    return enqueueFileShareMutation(fileId, async () => {
      const { data, error } = await client.PATCH(
        "/api/v1/files/{file_id}/users/{user_id}",
        {
          params: { path: { file_id: fileId, user_id: userId } },
          body: { role },
        },
      );
      if (error) throw error;
      if (!data) throw new Error("Failed to update share permission");
      await mutateFiles((current) =>
        current.map((file) => (file.id === fileId ? data : file)),
      );
      return data;
    });
  }

  async function removeShareAccess(fileId: number, userId: number) {
    return enqueueFileShareMutation(fileId, async () => {
      const { data, error } = await client.DELETE(
        "/api/v1/files/{file_id}/users/{user_id}",
        {
          params: { path: { file_id: fileId, user_id: userId } },
        },
      );
      if (error) throw error;
      if (!data) throw new Error("Failed to remove share access");
      await mutateFiles((current) =>
        current.map((file) => (file.id === fileId ? data : file)),
      );
      return data;
    });
  }

  async function deleteFile(fileId: number) {
    const { error } = await client.DELETE("/api/v1/files/{file_id}", {
      params: { path: { file_id: fileId } },
    });
    if (error) throw error;
    await mutateFiles((current) =>
      current.filter((file) => file.id !== fileId),
    );
    removeKnowledgeGuide(fileId);
  }

  async function listDeliveryEmailTemplates() {
    const generation = ++deliveryEmailTemplatesGeneration;
    deliveryEmailTemplatesLoading.value = true;
    try {
      const { data, error } = await client.GET(
        "/api/v1/files/delivery-email/templates",
      );
      if (error) throw error;
      if (generation !== deliveryEmailTemplatesGeneration) {
        return deliveryEmailTemplates.value;
      }
      deliveryEmailTemplates.value = (data ?? []).map(toDeliveryEmailTemplate);
      return deliveryEmailTemplates.value;
    } finally {
      if (generation === deliveryEmailTemplatesGeneration) {
        deliveryEmailTemplatesLoading.value = false;
      }
    }
  }

  async function createDeliveryEmailTemplate(payload: {
    name: string;
    subject: string;
    body: string;
  }) {
    deliveryEmailTemplatesGeneration += 1;
    const { data, error } = await client.POST(
      "/api/v1/files/delivery-email/templates",
      {
        body: {
          name: payload.name,
          subject: payload.subject,
          body: payload.body,
        },
      },
    );
    if (error) throw error;
    if (!data) throw new Error("Save failed");
    const template = toDeliveryEmailTemplate(data);
    deliveryEmailTemplates.value = [
      ...deliveryEmailTemplates.value.filter((row) => row.id !== template.id),
      template,
    ];
    return template;
  }

  async function updateDeliveryEmailTemplate(
    templateId: number,
    payload: {
      name?: string | null;
      subject?: string | null;
      body?: string | null;
    },
  ) {
    deliveryEmailTemplatesGeneration += 1;
    const { data, error } = await client.PATCH(
      "/api/v1/files/delivery-email/templates/{template_id}",
      {
        params: { path: { template_id: templateId } },
        body: {
          name: payload.name ?? null,
          subject: payload.subject ?? null,
          body: payload.body ?? null,
        },
      },
    );
    if (error) throw error;
    if (!data) throw new Error("Update failed");
    const template = toDeliveryEmailTemplate(data);
    deliveryEmailTemplates.value = deliveryEmailTemplates.value.map((row) =>
      row.id === template.id ? template : row,
    );
    return template;
  }

  async function deleteDeliveryEmailTemplate(templateId: number) {
    deliveryEmailTemplatesGeneration += 1;
    const { error } = await client.DELETE(
      "/api/v1/files/delivery-email/templates/{template_id}",
      {
        params: { path: { template_id: templateId } },
      },
    );
    if (error) throw error;
    deliveryEmailTemplates.value = deliveryEmailTemplates.value.filter(
      (row) => row.id !== templateId,
    );
  }

  async function getFileActivity(
    fileId: number,
  ): Promise<FileActivityResponse> {
    const { data, error } = await client.GET(
      "/api/v1/files/{file_id}/activity",
      {
        params: { path: { file_id: fileId } },
      },
    );
    if (error) throw error;
    if (!data) throw new Error("Failed to load file activity");
    return data;
  }

  async function recordSurveyLinkClick(fileId: number): Promise<void> {
    try {
      await client.POST("/api/v1/files/{file_id}/survey-link-click", {
        params: { path: { file_id: fileId } },
      });
    } catch {
      // Fire-and-forget: tracking must not interrupt Download Center navigation.
    }
  }

  async function getDeliveryEmailLastSend(): Promise<DeliveryEmailLastSend | null> {
    const { data, error, response } = await client.GET(
      "/api/v1/files/delivery-email/last-send",
    );
    if (response?.status === 404) return null;
    if (error) throw error;
    return data ?? null;
  }

  return {
    loading,
    loadingMore,
    hasMore,
    uploading,
    files,
    filterOptions,
    knowledgeGuides,
    knowledgeGuidesLoading,
    deliveryEmailTemplates,
    deliveryEmailTemplatesLoading,
    listFiles,
    listMoreFiles,
    listFileFilterOptions,
    resetFilesList,
    clearFiles,
    listKnowledgeGuides,
    renameKnowledgeGuideProduct,
    downloadFileById,
    openFileInNewTab,
    uploadDeliverable,
    uploadKnowledgeGuide,
    updateKnowledgeGuide,
    uploadExport,
    updateFileName,
    shareFile,
    updateSharePermission,
    removeShareAccess,
    deleteFile,
    getFileActivity,
    recordSurveyLinkClick,
    listDeliveryEmailTemplates,
    createDeliveryEmailTemplate,
    updateDeliveryEmailTemplate,
    deleteDeliveryEmailTemplate,
    getDeliveryEmailLastSend,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useFileStore, import.meta.hot));
}
